import { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { SWStateEvent, SWEventType } from '../service-worker/SWEventTypes';
import { Logger } from '../utils/Logger';
import { useEventTracking } from '../hooks/useEventTracking';
import { isSWAvailable } from '../config';

/**
 * Phase de l'état SW côté frontend.
 *
 * UNINITIALIZED : état initial — SW pas encore connecté (aucun message reçu)
 * LOADING       : initialize() en cours
 * SYNCING       : sync logements active
 * READY         : données disponibles
 * UNAVAILABLE   : jamais synchronisé + erreur
 * SW_UPDATING   : reload imminent
 */
export type SWPhase =
    | 'UNINITIALIZED'
    | 'LOADING'
    | 'SYNCING'
    | 'READY'
    | 'UNAVAILABLE'
    | 'SW_UPDATING';

/**
 * État SW complet côté frontend — source de vérité unique.
 *
 * Contrairement à un simple replace, chaque event SW ne met à jour que
 * les champs qu'il porte (update partiel via spread). Conséquences :
 * - pendingCount persiste à travers toutes les transitions (pas perdu pendant SYNCING)
 * - logementsSync est non-null uniquement pendant une sync active
 * - documentsSync est non-null uniquement pendant une sync de documents active
 *
 * Sans SW (navigateur non supporté ou isSWAvailable = false) :
 * l'état initial est READY et aucun message ne sera jamais reçu.
 */
export interface AppSWState {
    phase: SWPhase;
    pendingCount: number;
    logementsSync: {
        cursor: string;
        min: string;
        itemCount: number;
    } | null;
    documentsSync: {
        current: number;
        total: number;
    } | null;
}

const initialAppSWState: AppSWState = {
    phase: 'UNINITIALIZED',
    pendingCount: 0,
    logementsSync: null,
    documentsSync: null,
};

/**
 * Reducer accumulateur : chaque event met à jour uniquement les champs pertinents.
 *
 * UI states → transition de phase + mise à jour des champs associés.
 * INITIALIZATION_FAILED → UNAVAILABLE (déclenche le bouton réessayer).
 * CLEAR_STARTED / CLEAR_COMPLETED → LOADING + reset pendingCount (logout/context change).
 * DOCUMENTS_SYNC_PROGRESS / COMPLETED → accumulation dans documentsSync, phase inchangée.
 * Tous les autres events (telemetry) → state inchangé.
 *
 * Exporté pour les tests unitaires.
 */
export function swStateReducer(state: AppSWState, event: SWStateEvent): AppSWState {
    switch (event.type) {
        case SWEventType.LOADING:
            return { ...state, phase: 'LOADING', logementsSync: null };
        case SWEventType.SYNCING:
            return {
                ...state,
                phase: 'SYNCING',
                logementsSync: {
                    cursor: event.details.logEntryCreatedDate,
                    min: event.details.minLogEntryCreatedDate,
                    itemCount: event.details.itemCount,
                },
            };
        case SWEventType.READY:
            return {
                ...state,
                phase: 'READY',
                pendingCount: event.details.pendingCount,
                logementsSync: null,
            };
        case SWEventType.UNAVAILABLE:
            return { ...state, phase: 'UNAVAILABLE', logementsSync: null };
        case SWEventType.SW_UPDATING:
            return { ...state, phase: 'SW_UPDATING' };
        case SWEventType.INITIALIZATION_FAILED:
            return { ...state, phase: 'UNAVAILABLE', logementsSync: null };
        case SWEventType.CLEAR_STARTED:
        case SWEventType.CLEAR_COMPLETED:
            return { ...state, phase: 'LOADING', pendingCount: 0, logementsSync: null, documentsSync: null };
        case SWEventType.DOCUMENTS_SYNC_PROGRESS:
            return { ...state, documentsSync: { current: event.details.current, total: event.details.total } };
        case SWEventType.DOCUMENTS_SYNC_COMPLETED:
            return { ...state, documentsSync: null };
        default:
            // Telemetry-only events (LOGEMENTS_SYNC_STARTED/COMPLETED/FAILED, INITIALIZATION_STARTED/COMPLETED, etc.)
            // ne portent aucune donnée UI pertinente — state inchangé.
            return state;
    }
}

interface SWStateContextValue {
    state: AppSWState;
}

const SWStateContext = createContext<SWStateContextValue | undefined>(undefined);

export const useSWState = (): SWStateContextValue => {
    const context = useContext(SWStateContext);
    if (!context) {
        throw new Error('useSWState must be used within SWStateProvider');
    }
    return context;
};

interface SWStateProviderProps {
    children: ReactNode;
}

export const SWStateProvider = ({ children }: SWStateProviderProps) => {
    // Sans SW : phase READY dès le départ, aucun message ne sera jamais reçu
    const initial: AppSWState = isSWAvailable
        ? initialAppSWState
        : { ...initialAppSWState, phase: 'READY' };

    const [state, dispatch] = useReducer(swStateReducer, initial);
    const { trackSWStateEvent, trackSWError, trackUnhandledRejection, trackActivatedOnLoad, trackColdStart } = useEventTracking();

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // SW_STATE_SNAPSHOT : réponse ciblée à REQUEST_SW_STATE (client source uniquement)
            // SW_STATE_EVENT    : push à l'initiative du SW (tous les clients)
            if (event.data && (event.data.type === 'SW_STATE_SNAPSHOT' || event.data.type === 'SW_STATE_EVENT')) {
                const payload: SWStateEvent = event.data.payload;
                Logger.debug('SWStateContext', `📢 ${event.data.type}: ${payload.type}`, payload);
                trackSWStateEvent(payload);
                dispatch(payload);
            }

            if (event.data && event.data.type === 'SW_ERROR') {
                const { message, filename, lineno, colno, stack } = event.data.payload;
                Logger.error('SWStateContext', '🚨 SW_ERROR received:', { message, filename, lineno, colno });
                trackSWError(message, filename, lineno, colno, stack);
            }

            if (event.data && event.data.type === 'SW_UNHANDLED_REJECTION') {
                const { reason, stack } = event.data.payload;
                Logger.error('SWStateContext', '🚨 SW_UNHANDLED_REJECTION received:', { reason });
                trackUnhandledRejection(reason, stack);
            }

            // SW_ACTIVATED_ON_LOAD : SW vient de prendre le contrôle (activate event + clients.claim())
            if (event.data && event.data.type === 'SW_ACTIVATED_ON_LOAD') {
                Logger.debug('SWStateContext', '✅ SW_ACTIVATED_ON_LOAD received');
                trackActivatedOnLoad();
            }

            // SW_COLD_START : premier message reçu après (re)démarrage du processus SW
            if (event.data && event.data.type === 'SW_COLD_START') {
                Logger.debug('SWStateContext', '🥶 SW_COLD_START received - processus SW redémarré');
                trackColdStart();
            }
        };

        // Resync SW state when page returns from background (screen wake on mobile)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                Logger.debug('SWStateContext', '📡 Page visible again - refreshing SW state...');
                navigator.serviceWorker.controller?.postMessage({ type: 'REQUEST_SW_STATE' });
            }
        };

        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', handleMessage);

            // Au démarrage, demander l'état actuel du Service Worker
            // (évite de perdre les messages émis avant que le listener soit enregistré)
            Logger.debug('SWStateContext', '📡 Requesting current SW state...');
            navigator.serviceWorker.controller?.postMessage({ type: 'REQUEST_SW_STATE' });
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', handleMessage);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [trackSWStateEvent, trackSWError, trackUnhandledRejection, trackActivatedOnLoad, trackColdStart]);

    return (
        <SWStateContext.Provider value={{ state }}>
            {children}
        </SWStateContext.Provider>
    );
};
